import { TreeLineReceiptDetails } from '@/models/TreeLineReceiptDetails';
import { PaymentType } from '@/models/Payment';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import React from 'react';
import { isEmail } from '@/utils/emailUtil';
import {
  TreeLineBillingFieldConfig,
  TreeLineManualBillingAddress,
  TreeLinePreviewFieldConfig,
  TreeLinePreviewFieldKey,
} from './types';

type Props = {
  treeLineReceiptApplicationId: number;
  setTreeLineReceiptApplicationId: React.Dispatch<React.SetStateAction<number>>;
  treeLineReceiptLocaleCode: string;
  setTreeLineReceiptLocaleCode: React.Dispatch<React.SetStateAction<string>>;
  treeLineManualBillingAddress: TreeLineManualBillingAddress;
  setTreeLineManualBillingAddress: React.Dispatch<
    React.SetStateAction<TreeLineManualBillingAddress>
  >;
  localeOptions: ReadonlyArray<{ value: string; label: string }>;
  treeLinePreviewFields: TreeLinePreviewFieldConfig[];
  treeLineBillingFields: TreeLineBillingFieldConfig[];
  treeLineReceiptPreview: TreeLineReceiptDetails;
  treeLineFieldOverrides: Record<TreeLinePreviewFieldKey, boolean>;
  updateTreeLinePreviewField: (field: TreeLinePreviewFieldKey, value: string) => void;
  toggleTreeLineFieldOverride: (field: TreeLinePreviewFieldKey) => void;
  loadingTreeLineReceiptPreview: boolean;
};

const TreeLineReceiptForm: React.FC<Props> = ({
  treeLineReceiptApplicationId,
  setTreeLineReceiptApplicationId,
  treeLineReceiptLocaleCode,
  setTreeLineReceiptLocaleCode,
  treeLineManualBillingAddress,
  setTreeLineManualBillingAddress,
  localeOptions,
  treeLinePreviewFields,
  treeLineBillingFields,
  treeLineReceiptPreview,
  treeLineFieldOverrides,
  updateTreeLinePreviewField,
  toggleTreeLineFieldOverride,
  loadingTreeLineReceiptPreview,
}) => {
  const [showAutoPopulatedFields, setShowAutoPopulatedFields] = React.useState<boolean>(false);

  const paymentTypeOptions = React.useMemo(() => {
    return Object.values(PaymentType).filter(
      (value): value is PaymentType => typeof value === 'number'
    );
  }, []);

  const autoPopulatedStatus = React.useMemo(() => {
    if (treeLineReceiptApplicationId <= 0) {
      return {
        label: 'Waiting for applicationId',
        color: 'warning' as const,
      };
    }

    const hasMissingField = treeLinePreviewFields
      .filter((fieldConfig) => fieldConfig.required)
      .some((fieldConfig) => {
        if (fieldConfig.key === 'year') {
          return treeLineReceiptPreview.year == null || treeLineReceiptPreview.year <= 0;
        }

        if (fieldConfig.key === 'paymentType') {
          const paymentTypeValue = treeLineReceiptPreview.paymentType;

          if (paymentTypeValue == null) {
            return true;
          }

          return !Number.isFinite(Number(paymentTypeValue));
        }

        const fieldKey = fieldConfig.key as keyof TreeLineReceiptDetails;
        const fieldValue = treeLineReceiptPreview[fieldKey];

        if (typeof fieldValue === 'string') {
          return fieldValue.trim() === '';
        }

        return true;
      });

    if (hasMissingField) {
      return {
        label: 'Has missing fields',
        color: 'error' as const,
      };
    }

    if (!isEmail(treeLineReceiptPreview.email)) {
      return {
        label: 'Invalid email',
        color: 'error' as const,
      };
    }

    return {
      label: 'Complete',
      color: 'success' as const,
    };
  }, [treeLinePreviewFields, treeLineReceiptApplicationId, treeLineReceiptPreview]);

  const billingInfoStatus = React.useMemo(() => {
    const hasMissingField = treeLineBillingFields.some(
      (fieldConfig: TreeLineBillingFieldConfig) => {
        if (fieldConfig.required !== true) {
          return false;
        }

        const fieldKey: keyof TreeLineManualBillingAddress = fieldConfig.key;
        return treeLineManualBillingAddress[fieldKey].trim() === '';
      }
    );

    if (hasMissingField) {
      return {
        label: 'Has missing fields',
        color: 'error' as const,
      };
    }

    return {
      label: 'Complete',
      color: 'success' as const,
    };
  }, [treeLineBillingFields, treeLineManualBillingAddress]);

  const getStatusChipSx = (statusColor: 'warning' | 'error' | 'success') => {
    return (theme: Theme) => ({
      backgroundColor:
        statusColor === 'success'
          ? theme.palette.successBackground.main
          : statusColor === 'error'
            ? theme.palette.errorBackground.main
            : theme.palette.warningBackground.main,
      color:
        statusColor === 'success'
          ? theme.palette.successText.main
          : statusColor === 'error'
            ? theme.palette.errorText.main
            : theme.palette.warningText.main,
      border: '1px solid',
      borderColor:
        statusColor === 'success'
          ? theme.palette.successBorder.main
          : statusColor === 'error'
            ? theme.palette.errorBorder.main
            : theme.palette.warningBorder.main,
    });
  };

  return (
    <Stack spacing={2}>
      <FormControl fullWidth required>
        <InputLabel id='tree-line-locale-code-label'>Locale Code</InputLabel>
        <Select
          labelId='tree-line-locale-code-label'
          value={treeLineReceiptLocaleCode}
          label='Locale Code'
          onChange={(e) => setTreeLineReceiptLocaleCode(e.target.value)}
        >
          <MenuItem value=''>Select Locale</MenuItem>
          {localeOptions.map((localeOption) => (
            <MenuItem key={localeOption.value} value={localeOption.value}>
              {localeOption.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        required
        type='number'
        label='Application Id'
        value={treeLineReceiptApplicationId}
        onChange={(e) => setTreeLineReceiptApplicationId(Number(e.target.value))}
        slotProps={{
          htmlInput: {
            onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
          },
        }}
        sx={{
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
            {
              WebkitAppearance: 'none',
              margin: 0,
            },
        }}
      />
      <Box
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2,
        }}
      >
        <Stack spacing={2}>
          <Box
            display='flex'
            flexDirection='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography variant='subtitle2'>Auto-populated fields</Typography>
              <Chip
                label={autoPopulatedStatus.label}
                color={autoPopulatedStatus.color}
                size='small'
                sx={getStatusChipSx(autoPopulatedStatus.color)}
              />
            </Stack>
            <Button
              variant='secondary'
              size='small'
              onClick={() => setShowAutoPopulatedFields((prev) => !prev)}
            >
              {showAutoPopulatedFields ? 'Collapse' : 'Expand'}
            </Button>
          </Box>
          <Typography variant='body2' color='textSecondary'>
            {loadingTreeLineReceiptPreview
              ? 'Loading details from Application Id...'
              : 'Fields are auto-populated from Application Id.'}
          </Typography>
          <Collapse in={showAutoPopulatedFields}>
            <Stack spacing={2}>
              {treeLinePreviewFields.map((fieldConfig) => {
                const fieldValue =
                  fieldConfig.key === 'year'
                    ? treeLineReceiptPreview.year !== null && treeLineReceiptPreview.year > 0
                      ? treeLineReceiptPreview.year
                      : ''
                    : fieldConfig.key === 'amountPaid'
                      ? treeLineReceiptPreview.amountPaid > 0
                        ? treeLineReceiptPreview.amountPaid
                        : ''
                      : treeLineReceiptPreview[fieldConfig.key];

                const isPaymentTypeField = fieldConfig.key === 'paymentType';
                const isTransactionIdField = fieldConfig.key === 'transactionId';
                const isCustomerIdField = fieldConfig.key === 'customerId';
                const hasInvalidEmail =
                  fieldConfig.key === 'email' &&
                  fieldValue !== '' &&
                  !isEmail(String(fieldValue ?? ''));
                const numericPaymentTypeValue =
                  fieldValue == null ? Number.NaN : Number(fieldValue);
                const currentPaymentTypeValue: PaymentType | '' =
                  Number.isFinite(numericPaymentTypeValue) &&
                  (numericPaymentTypeValue === Number(PaymentType.CreditCard) ||
                    numericPaymentTypeValue === Number(PaymentType.OnAccount))
                    ? numericPaymentTypeValue
                    : '';
                const hasKnownPaymentTypeOption =
                  currentPaymentTypeValue !== '' &&
                  paymentTypeOptions.includes(currentPaymentTypeValue);

                const shouldShowTransactionId =
                  !isTransactionIdField ||
                  treeLineReceiptPreview.paymentType === PaymentType.CreditCard;

                const shouldShowCustomerId =
                  !isCustomerIdField ||
                  treeLineReceiptPreview.paymentType === PaymentType.OnAccount;

                if (!shouldShowTransactionId || !shouldShowCustomerId) {
                  return null;
                }

                return (
                  <Stack key={fieldConfig.key} direction='row' spacing={1} alignItems='center'>
                    {isPaymentTypeField ? (
                      <FormControl fullWidth required={fieldConfig.required}>
                        <InputLabel id='tree-line-payment-type-label'>
                          {fieldConfig.label}
                        </InputLabel>
                        <Select
                          labelId='tree-line-payment-type-label'
                          label={fieldConfig.label}
                          value={currentPaymentTypeValue}
                          onChange={(e) =>
                            updateTreeLinePreviewField(fieldConfig.key, String(e.target.value))
                          }
                          disabled={!treeLineFieldOverrides[fieldConfig.key]}
                        >
                          {!hasKnownPaymentTypeOption && currentPaymentTypeValue !== '' ? (
                            <MenuItem value={currentPaymentTypeValue}>
                              {String(currentPaymentTypeValue)}
                            </MenuItem>
                          ) : null}
                          {paymentTypeOptions.map((paymentTypeOption) => (
                            <MenuItem key={paymentTypeOption} value={paymentTypeOption}>
                              {PaymentType[paymentTypeOption]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        fullWidth
                        required={fieldConfig.required}
                        type={fieldConfig.type ?? 'text'}
                        label={fieldConfig.label}
                        value={fieldValue}
                        error={hasInvalidEmail}
                        helperText={
                          hasInvalidEmail ? 'Please enter a valid email address.' : undefined
                        }
                        onChange={(e) =>
                          updateTreeLinePreviewField(fieldConfig.key, e.target.value)
                        }
                        InputProps={{ readOnly: !treeLineFieldOverrides[fieldConfig.key] }}
                      />
                    )}
                    <Button
                      variant='secondary'
                      size='small'
                      onClick={() => toggleTreeLineFieldOverride(fieldConfig.key)}
                      sx={{
                        minWidth: 84,
                        height: 40,
                        px: 1.25,
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {treeLineFieldOverrides[fieldConfig.key] ? 'Lock' : 'Override'}
                    </Button>
                  </Stack>
                );
              })}
            </Stack>
          </Collapse>
        </Stack>
      </Box>
      <Box
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2,
        }}
      >
        <Stack spacing={2}>
          <Box display='flex' flexDirection='row' gap={1} alignItems='center'>
            <Typography variant='subtitle2'>Billing Information</Typography>
            <Chip
              label={billingInfoStatus.label}
              color={billingInfoStatus.color}
              size='small'
              sx={getStatusChipSx(billingInfoStatus.color)}
            />
          </Box>
          {treeLineBillingFields.map((fieldConfig: TreeLineBillingFieldConfig) => {
            const fieldKey: keyof TreeLineManualBillingAddress = fieldConfig.key;

            return (
              <TextField
                key={fieldKey}
                fullWidth
                required={fieldConfig.required}
                label={fieldConfig.label}
                value={treeLineManualBillingAddress[fieldKey]}
                onChange={(e) =>
                  setTreeLineManualBillingAddress((prev) => ({
                    ...prev,
                    [fieldKey]: e.target.value,
                  }))
                }
              />
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};

export default TreeLineReceiptForm;
